/**
 * @fileoverview AudioModal Component - Full-screen audio player modal
 * 
 * Provides a comprehensive audio player interface with:
 * - Full-screen modal audio player
 * - Support for both direct audio files and embedded platforms
 * - Waveform visualization with animations
 * - Complete audio controls (play/pause, seek, volume, skip)
 * - Time display and progress tracking
 * - Minimize to mini-player functionality
 * - Platform-specific embedded players (Spotify, SoundCloud, etc.)
 * - Loading states and error handling
 * - Audio metadata display
 * 
 * @component
 * @example
 * <AudioModal />
 * 
 * @requires React
 * @requires ./LoadingSpinner
 * @requires ../../contexts/AudioContext
 * @requires ./AudioModal.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025    
 */

import React from 'react';
import LoadingSpinner from './LoadingSpinner';
import { useAudio } from '../../contexts/AudioContext';
import './AudioModal.css';

/**
 * AudioModal Component
 * 
 * Renders a full-screen audio player modal with comprehensive playback controls.
 * Supports both direct audio files and embedded platform players.
 * 
 * Features:
 * - Full-screen modal interface
 * - Waveform visualization with animations
 * - Complete audio controls (play, pause, seek, volume)
 * - Skip forward/backward (15 seconds)
 * - Mini-player mode toggle
 * - Platform-specific embedded players
 * - Time display and progress tracking
 * - Audio metadata display
 * - Loading states and error handling
 * 
 * @component
 * @returns {JSX.Element|null} Full-screen audio player modal or null if hidden
 */
const AudioModal = () => {
    const {
        currentAudio,
        isPlaying,
        currentTime,
        duration,
        volume,
        isEmbedded,
        isLoading,
        showFullModal,
        audioRef,
        closeAudio,
        minimizeToMini,
        togglePlayback,
        setCurrentTime,
        setDuration,
        setVolume,
        setIsPlaying,
        setIsLoading,
        getEmbeddableUrl,
        formatTime
    } = useAudio();



    if (!showFullModal || !currentAudio) {
        return null;
    }

    
    const handleAudioSeek = (audioElement, newTime) => {
        audioElement.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleAudioVolumeChange = (audioElement, newVolume) => {
        audioElement.volume = newVolume;
        setVolume(newVolume);
    };

        return (
        <>
            {showFullModal && (
                <div className="audio-modal-overlay" onClick={closeAudio}>
                    <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="audio-modal-header">
                            <h3>{currentAudio.topic}</h3>
                            <div className="audio-modal-header-controls">
                                {/* Only show minimize button for direct audio files (MP3, WAV, etc.) */}
                                {!isEmbedded && (
                                    <button 
                                        className="header-btn minimize" 
                                        onClick={minimizeToMini}
                                        title="Minimize player"
                                    >
                                        −
                                    </button>
                                )}
                                <button className="header-btn close" onClick={closeAudio}>
                                    ×
                                </button>
                            </div>
                        </div>
                    
                    <div className="audio-modal-content">
                        {isLoading && (
                            <div className="media-loading">
                                <LoadingSpinner />
                            </div>
                        )}
                        
                        {isEmbedded ? (
                            /* Embedded Platform Audio - show the actual player */
                            <div className="embedded-audio-player">
                                <iframe
                                    src={getEmbeddableUrl(currentAudio.webURLLink)}
                                    width="100%"
                                    height="352"
                                    frameBorder="0"
                                    allowtransparency="true"
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    loading="lazy"
                                    title={currentAudio.topic}
                                    onLoad={() => {
                                        setIsLoading(false);
                                    }}
                                ></iframe>
                            </div>
                    ) : (
                        /* Standard Audio Element with Waveform */
                        <div className="audio-waveform-container">
                            <div className="audio-waveform">
                                <i className="fas fa-music audio-icon"></i>
                                <div className="waveform-bars">
                                    {[...Array(20)].map((_, i) => (
                                        <div 
                                            key={i} 
                                            className={`waveform-bar ${isPlaying ? 'animating' : ''}`}
                                            style={{
                                                animationDelay: `${i * 0.1}s`,
                                                height: `${Math.random() * 40 + 20}px`
                                            }}
                                        ></div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Audio Element is now persistent in AudioContext */}
                        </div>
                    )}

                    {/* Audio Controls - Only show for regular audio files, not embedded platforms */}
                    {!isEmbedded && (
                        <div className="audio-controls">
                            <div className="main-controls">
                                <button 
                                    className="control-btn skip-back"
                                    onClick={() => {
                                        if (audioRef.current) {
                                            handleAudioSeek(audioRef.current, Math.max(0, currentTime - 15));
                                        }
                                    }}
                                    title="Skip back 15s"
                                >
                                    <i className="fas fa-backward"></i>
                                    <span>15</span>
                                </button>
                                
                                <button 
                                    className={`control-btn play-pause ${isPlaying ? 'playing' : ''}`}
                                    onClick={togglePlayback}
                                >
                                    <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
                                </button>
                                
                                <button 
                                    className="control-btn mini-player-btn"
                                    onClick={minimizeToMini}
                                    title="Play in Mini Player"
                                >
                                    <i className="fas fa-compress-alt"></i>
                                </button>
                                
                                <button 
                                    className="control-btn skip-forward"
                                    onClick={() => {
                                        if (audioRef.current) {
                                            handleAudioSeek(audioRef.current, Math.min(duration, currentTime + 15));
                                        }
                                    }}
                                    title="Skip forward 15s"
                                >
                                    <i className="fas fa-forward"></i>
                                    <span>15</span>
                                </button>
                            </div>

                            {/* Progress Bar */}
                            <div className="progress-container">
                                <span className="time-display current-time">
                                    {formatTime(currentTime)}
                                </span>
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill"
                                        style={{ 
                                            width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' 
                                        }}
                                    ></div>
                                    <input
                                        type="range"
                                        className="progress-slider"
                                        min="0"
                                        max={duration || 0}
                                        value={currentTime}
                                        onChange={(e) => {
                                            if (audioRef.current) {
                                                handleAudioSeek(audioRef.current, parseFloat(e.target.value));
                                            }
                                        }}
                                    />
                                </div>
                                <span className="time-display total-time">
                                    {formatTime(duration)}
                                </span>
                            </div>

                            {/* Volume Control */}
                            <div className="volume-control">
                                <i className={`fas ${volume === 0 ? 'fa-volume-mute' : volume < 0.5 ? 'fa-volume-down' : 'fa-volume-up'}`}></i>
                                <input
                                    type="range"
                                    className="volume-slider"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={volume}
                                    onChange={(e) => {
                                        if (audioRef.current) {
                                            handleAudioVolumeChange(audioRef.current, parseFloat(e.target.value));
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Audio Info Section */}
                    <div className="audio-modal-info">
                        <p>{currentAudio.shortDescription}</p>
                        <div className="audio-modal-meta">
                            {currentAudio.duration && (
                                <span><i className="fas fa-clock"></i> {currentAudio.duration}</span>
                            )}
                            {currentAudio.publishDate && !isNaN(new Date(currentAudio.publishDate).getTime()) && (
                                <span><i className="fas fa-calendar"></i> {new Date(currentAudio.publishDate).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                })}</span>
                            )}
                            {currentAudio.entity && (
                                <span><i className="fas fa-building"></i> {currentAudio.entity}</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
            )}
        </>
    );
};

export default AudioModal; 